import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Clock, Plus, Pencil, Trash2, Users, MapPin } from 'lucide-react';

const EMPTY_SHIFT = { name: '', start: '09:00', end: '18:00', grace_minutes: 15, location: 'UAE' };

export default function ShiftMasterPage() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_SHIFT });
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState([]);
  const [employeeCounts, setEmployeeCounts] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const [shiftsRes, countriesRes, assignmentsRes] = await Promise.all([
        api.get('/hr/shifts'),
        api.get('/hr/countries').catch(() => ({ data: [] })),
        api.get('/hr/shift-assignments').catch(() => ({ data: [] })),
      ]);
      setShifts(shiftsRes.data || []);
      setCountries(countriesRes.data || []);
      // Count employees per shift
      const counts = {};
      (assignmentsRes.data || []).forEach(emp => {
        const sid = emp.shift_id || 'unassigned';
        counts[sid] = (counts[sid] || 0) + 1;
      });
      setEmployeeCounts(counts);
    } catch {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_SHIFT });
    setDialogOpen(true);
  }

  function openEdit(shift) {
    setEditing(shift);
    setForm({
      name: shift.name || '',
      start: shift.start || '09:00',
      end: shift.end || '18:00',
      grace_minutes: shift.grace_minutes ?? 15,
      location: shift.location || 'UAE',
    });
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!form.name.trim()) { toast.error('Shift name is required'); return; }
    if (!form.start || !form.end) { toast.error('Start and end times are required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/hr/shifts/${editing.id}`, form);
        toast.success('Shift updated');
      } else {
        await api.post('/hr/shifts', form);
        toast.success('Shift created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save shift');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(shift) {
    if (!window.confirm(`Delete "${shift.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/hr/shifts/${shift.id}`);
      toast.success('Shift deleted');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete shift');
    }
  }

  function formatTime(t) {
    if (!t) return '--:--';
    const [h, m] = t.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${h12}:${m} ${ampm}`;
  }

  function calcDuration(start, end) {
    if (!start || !end) return '-';
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins < 0) mins += 24 * 60;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  }

  return (
    <div className="space-y-6" data-testid="shift-master-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Shift Master</h1>
          <p className="text-muted-foreground text-sm mt-1">Define and manage work shifts for your organization</p>
        </div>
        <Button onClick={openCreate} data-testid="create-shift-btn">
          <Plus className="h-4 w-4 mr-2" /> Create Shift
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : shifts.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Clock className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-semibold mb-1">No Shifts Defined</h3>
            <p className="text-muted-foreground text-sm mb-4">Create your first shift to get started.</p>
            <Button onClick={openCreate}>Create Shift</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {shifts.map(shift => (
            <Card key={shift.id} className="group hover:border-primary/30 transition-colors" data-testid={`shift-card-${shift.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{shift.name}</CardTitle>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(shift)} data-testid={`edit-shift-${shift.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => handleDelete(shift)} data-testid={`delete-shift-${shift.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{formatTime(shift.start)}</span>
                    <span className="text-muted-foreground">to</span>
                    <span className="font-medium">{formatTime(shift.end)}</span>
                  </div>
                  <Badge variant="outline" className="text-xs">{calcDuration(shift.start, shift.end)}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{shift.location || 'UAE'}</span>
                  </div>
                  <Badge variant="outline" className="text-xs">Grace: {shift.grace_minutes ?? 15}min</Badge>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground pt-1 border-t">
                  <Users className="h-4 w-4" />
                  <span>{employeeCounts[shift.id] || 0} employee(s) assigned</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="shift-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Shift' : 'Create New Shift'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Shift Name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Morning Shift" data-testid="shift-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Input type="time" value={form.start} onChange={e => setForm(f => ({ ...f, start: e.target.value }))} data-testid="shift-start-input" />
              </div>
              <div>
                <Label>End Time</Label>
                <Input type="time" value={form.end} onChange={e => setForm(f => ({ ...f, end: e.target.value }))} data-testid="shift-end-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Grace Period (minutes)</Label>
                <Input type="number" min={0} max={120} value={form.grace_minutes} onChange={e => setForm(f => ({ ...f, grace_minutes: parseInt(e.target.value) || 0 }))} data-testid="shift-grace-input" />
              </div>
              <div>
                <Label>Location</Label>
                <Select value={form.location} onValueChange={v => setForm(f => ({ ...f, location: v }))}>
                  <SelectTrigger data-testid="shift-location-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(countries.length > 0 ? countries : ['UAE', 'India']).map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-shift-btn">
              {saving ? 'Saving...' : editing ? 'Update Shift' : 'Create Shift'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
